<template>
  <div ref="triggerElement">
    <div :class="[$style.spriteInput, noBorder ? $style.noBorder : '']" @click="togglePanel($event)">
      <div :class="$style.spriteDisplay">
        <Sprite v-if="modelValue" :sprite="modelValue" />
        <span v-else :class="$style.noSpriteText">Select a sprite</span>
      </div>
      <i
        :class="[isPanelVisible ? 'pi pi-chevron-up' : 'pi pi-chevron-down', disabled ? $style.panelDisabled : '']"
      ></i>
    </div>
    <Popover
      ref="overlayPanel"
      :style="{ minWidth: minWidth + 'px' }"
      :dismissable="true"
      @show="onPanelShow"
      @hide="onPanelHide"
    >
      <div>
        <FileUpload
          :class="$style.uploadButton"
          :max-file-size="4000000"
          mode="basic"
          name="files[]"
          accept="image/*"
          multiple
          choose-label="Upload sprites"
          custom-upload
          :auto="true"
          @uploader="uploadFilesHandler"
        />
      </div>
      <hr :class="$style.divider" />
      <div :class="$style.noSprite" @click="handleClickOnNoSprite">No Sprite</div>
      <div v-for="option in options" :key="option.id" :class="$style.spriteWrapper" @click="() => handleSelect(option)">
        <Sprite :sprite="option" />
        <VdIcon store="fas" name="trash" :class="$style.deleteBtn" @click.stop="handleDelete(option)" />
      </div>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import Sprite from "@/components/Sprite";
import { StoreItemSprite } from "@/graphql";
import FileUpload from "primevue/fileupload";
import Popover from "primevue/popover";
import type { PropType } from "vue";
import { onMounted, ref } from "vue";

const emit = defineEmits(["upload", "delete", "select", "link", "unlinkSprite"]);

const overlayPanel = ref<InstanceType<typeof Popover> | null>(null);
const triggerElement = ref<HTMLElement | null>(null);
const minWidth = ref(0);
const isPanelVisible = ref(false);

const props = defineProps({
  options: {
    type: Array as PropType<StoreItemSprite[]>,
    required: true,
    default: () => [],
  },
  storeItemId: {
    type: String as PropType<string>,
    default: null,
  },
  modelValue: {
    type: Object as PropType<{ id: string; name: string; imageUrl: string }> | null,
    default: null,
  },
  uploadInProgress: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: false,
  },
  noBorder: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  disabled: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
});

const togglePanel = (event: MouseEvent) => {
  if (props.disabled) {
    return;
  }

  if (triggerElement.value) {
    minWidth.value = triggerElement.value.offsetWidth;
  }
  overlayPanel.value?.toggle(event);
};

const handleDelete = (sprite: StoreItemSprite) => {
  emit("delete", sprite.id, sprite.name);
};

const handleSelect = (sprite: StoreItemSprite) => {
  if (!props.storeItemId) {
    emit("select", sprite);
  } else {
    emit("link", props.storeItemId, sprite.id);
  }
  overlayPanel.value?.hide();
};

const uploadFilesHandler = (event) => {
  emit("upload", event);
};

const handleClickOnNoSprite = () => {
  if (!props.storeItemId) {
    emit("select", null);
  } else {
    emit("unlinkSprite", props.storeItemId);
  }
  overlayPanel.value?.hide();
};

const onPanelShow = () => {
  isPanelVisible.value = true;
};

const onPanelHide = () => {
  isPanelVisible.value = false;
};

onMounted(() => {
  if (triggerElement.value) {
    minWidth.value = triggerElement.value.offsetWidth;
  }
});
</script>

<style module>
.spriteInput {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.4rem;
  cursor: pointer;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  height: 40px;
}

.spriteInput:hover {
  border-color: #80bdff;
}

.noBorder {
  border: none;
}

.spriteDisplay {
  display: flex;
  align-items: center;
}

.noSpriteText {
  color: gray;
  margin-left: 10px;
  font-weight: 400;
}

.spriteWrapper {
  display: flex;
  align-items: center;
  padding: 5px;
  cursor: pointer;
  border-radius: 5px;
  transition: background-color 0.3s ease;
  justify-content: space-between;
}

.spriteWrapper:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.noSprite {
  display: flex;
  align-items: center;
  padding: 5px;
  width: 100%;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 0.6rem 0.5rem;
}

.noSprite:hover {
  background-color: #f2f3f5;
}

.selectionDropdown {
  background-color: white;
  position: absolute;
  z-index: 1;
  width: 240px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #ccc;
  padding: 1rem;
  margin-top: 5px;
  border-radius: 0.25rem;
}

.uploadButton {
  width: 100%;
  background-color: #ffffff;
  color: #606266;
  border: none;
  margin-bottom: 0.5rem;
  transition: background-color 0.3s ease;
}

.uploadButton:hover {
  background-color: #f2f3f5;
}

.divider {
  margin: 0.5rem 0;
  border: none;
  border-top: 1px solid #ccc;
}

.deleteBtn {
  cursor: pointer;
}

.panelDisabled {
  opacity: 0.5;
}
</style>
